import React, {useEffect, useMemo} from "react";
import {useQuery} from "@apollo/client";
import gql from "graphql-tag";
import {useHistory} from "react-router";
import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import AppLayout from "../../layouts/AppLayout";
import AccountListTable from "../../components/AccountListTable";
import {AccountOrderByInput} from "../../schema";
import {SortingRule} from "react-table";
import Table, {TableFilter} from "../../ui/table/Table";
import H1 from "../../ui/H1";
import {reverse} from "../../urls";
import {CheckboxFilterBlock} from "../../ui/table/filter/UICheckboxFilterBlock";
import {FilterState} from "../../ui/table/TableFilter";
import PrimaryButton from "../../ui/PrimaryButton";

export default function AccountListScene() {
  const history = useHistory();

  const skipPageResetRef = React.useRef<boolean>()
  const [accounts, setAccounts] = React.useState<AccountType[]>([])
  const [totalCount, setTotalCount] = React.useState<number>(1)
  const [skip, setSkip] = React.useState<number>(0)
  const [first, setFirst] = React.useState<number>(25)
  const [sortBy, setSortBy] = React.useState<AccountOrderByInput>(AccountOrderByInput.name_ASC)
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined)

  const accountFilters = new CheckboxFilterBlock('accountType', 'Account Type', [
    {
      id: 'trainer',
      value: 'trainer',
      label: 'Trainer',
      checked: false,
    },
    {
      id: 'brand',
      value: 'brand',
      label: 'Brand',
      checked: false,
    },
    {
      id: 'company',
      value: 'company',
      label: 'Company',
      checked: false,
    },
  ]);
  const [states, setStates] = React.useState<FilterState[]>([new FilterState(accountFilters.id, false, '')]);
  const typesFilter = states.find(x => x.id === accountFilters.id && x.active)?.value
  const [skipDataLoad, setSkipDataLoad] = React.useState<boolean>(true);

  const {data, loading, error} = useQuery<AccountQuery>(query, {
    skip: skipDataLoad,
    fetchPolicy: 'cache-and-network',
    variables: {skip, first, sortBy, searchQuery, filterByTypes: typesFilter},
  });

  useMemo(() => {
    if (data) {
      setAccounts(data.feed.accounts);
      setTotalCount(data.feed.total);
    }
  }, [data])

  const onFetchData = (pageIndex: number, newPageSize: number, newSortBy: SortingRule<AccountType>[], filter: TableFilter | null) => {
    skipPageResetRef.current = true;
    setSkip(pageIndex * newPageSize);
    setFirst(newPageSize);
    const value = newSortBy.find((x) => x.id === 'name')?.desc;
    setSearchQuery(filter?.search);
    setSortBy(value ? AccountOrderByInput.name_DESC : AccountOrderByInput.name_ASC);
    setStates(filter?.filters || []);
    setSkipDataLoad(false);
  }

  const onClick = (account: AccountType) => {
    history.push(reverse('accounts_detail', {id: account.id}));
  }

  useEffect(() => {
    skipPageResetRef.current = false;
  }, []);

  const columns = React.useMemo(() => [
    {
      Header: 'Accounts',
      columns: [
        {
          Header: 'Account name',
          accessor: 'name',
          filter: 'fuzzyText',
          defaultSort: 'desc',
          sortType: 'basic',
        },
        {
          Header: 'Type',
          accessor: '__typename',
          filter: 'fuzzyText',
          disableSortBy: true,
        },
        {
          Header: 'Trial',
          accessor: 'isTrial',
          disableSortBy: true,
        },
        {
          Header: 'ID',
          accessor: 'id',
          disableSortBy: true,
        }]
    }
  ].flatMap((c: any) => c.columns), []);

  const buttons =
          <PrimaryButton onClick={() => window.location.assign('/company-form')}>Add Company</PrimaryButton>;

  return (
    <>
      <ReactDocumentTitle title='Accounts'/>

      <AppLayout className="">
        <div className="pt-8 pb-20">
          <H1>Accounts</H1>
          <div className="mt-6 mb-4">
            <Table<AccountType, TableFilter> name="accounts:v1" reset={skipPageResetRef.current || false}
                                             error={error?.message}
                                             loading={loading}
                                             total={totalCount}
                                             onFetchData={onFetchData} columns={columns}
                                             data={accounts}
                                             filters={[accountFilters]}
                                             buttons={buttons}
                                             onClick={onClick}/>
          </div>
        </div>
      </AppLayout>
    </>
  )
}

type AccountType = {
  id: string;
  name: string;
  __typename: string;
  isTrial: boolean;
}

type AccountFeed = {
  total: number;
  count: number;
  skip: number;
  accounts: AccountType[];
}
type AccountQuery = {
  feed: AccountFeed;
}


const query = gql`
  query AccountListQuery($searchQuery: String, $skip: Int!, $first: Int!, $orderBy: AccountOrderByInput, $filterByTypes: String) {
    feed: getAccountsFeed(searchBy: $searchQuery, skip: $skip, first: $first, orderBy: $orderBy, filterByTypes: $filterByTypes) {
      total
      count
      skip

      accounts {
        id
        ...AccountTableItem
      }
    }
  }

  ${AccountListTable.fragments.item}
`;
